/** Editor для Вимоги-накладної (списання матеріалів у виробництво, режим 1C_WIP). */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { departmentsApi } from '@/api/hr';
import { nomenclatureGroups } from '@/api/nomenclatureGroups';
import { accounts as accountsApi } from '@/api/documents';
import DatePopover from '@/components/DatePopover';
import {
  materialRequisitions,
  type MaterialRequisitionItemIn,
} from '@/api/materialRequisitions';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

const PERMISSION = 'production.material_transfer.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  source_warehouse_id: string;
  department_id: string;
  nomenclature_group_id: string;
  cost_account_code: string;
  comment: string;
  items: MaterialRequisitionItemIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  source_warehouse_id: '',
  department_id: '',
  nomenclature_group_id: '',
  cost_account_code: '',
  comment: '',
  items: [],
};

const newRow = (n: number): MaterialRequisitionItemIn => ({
  line_number: n, nomenclature_id: '', quantity: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function MaterialRequisitionEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.list({ limit: 500 }) });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const deptQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const groupQ = useQuery({
    queryKey: ['nomenclature-groups'],
    queryFn: () => nomenclatureGroups.list(),
  });
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const docQuery = useQuery({
    queryKey: ['material-requisition', id],
    queryFn: () => materialRequisitions.get(id!),
    enabled: !isNew,
  });
  const numberPreview = useNumberPreview('doc.material_requisitions', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        source_warehouse_id: d.source_warehouse_id,
        department_id: d.department_id ?? '',
        nomenclature_group_id: d.nomenclature_group_id ?? '',
        cost_account_code: d.cost_account_code ?? '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          quantity: it.quantity,
          account_code: it.account_code,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;
  // Запобіжник: у спрощеному режимі Вимога-накладна не застосовується (лишала б
  // залишок на рах.23). Блокуємо збереження/проведення + попереджаємо.
  const selectedOrg = orgList.data?.items.find((o) => o.id === form.organization_id);
  const isSimpleMode = selectedOrg?.production_costing_mode === 'SIMPLE';

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    source_warehouse_id: form.source_warehouse_id,
    department_id: form.department_id || null,
    nomenclature_group_id: form.nomenclature_group_id || null,
    cost_account_code: form.cost_account_code || null,
    comment: form.comment || null,
    items: form.items,
  });

  const create = useMutation({
    mutationFn: () => materialRequisitions.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['material-requisitions'] });
      navigate(`/material-requisitions/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => materialRequisitions.update(id!, buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-requisition', id] });
      qc.invalidateQueries({ queryKey: ['material-requisitions'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: () => materialRequisitions.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-requisition', id] });
      qc.invalidateQueries({ queryKey: ['material-requisitions'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => materialRequisitions.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-requisition', id] });
      qc.invalidateQueries({ queryKey: ['material-requisitions'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  function addItem() { setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] })); }
  function removeItem(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })),
    }));
  }
  function setItemField(i: number, patch: Partial<MaterialRequisitionItemIn>) {
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }

  const totalCost = useMemo(
    () =>
      (docQuery.data?.items ?? []).reduce(
        (acc, it) => acc + Number(it.total_cost ?? '0'),
        0,
      ),
    [docQuery.data],
  );

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  // Вибір рахунка з плану рахунків (замість ручного вводу — без помилок).
  const acctList = accountsQ.data ?? [];
  const acctSelect = (
    value: string | null | undefined, onChange: (v: string | null) => void, def: string,
  ) => (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}
            disabled={isPosted} style={{ width: '100%', minWidth: 120 }}>
      <option value="">— (деф. {def})</option>
      {acctList.map((a) => (<option key={a.id} value={a.code}>{a.code} — {a.name}</option>))}
    </select>
  );
  function validate(): string | null {
    if (!form.source_warehouse_id) return 'Оберіть склад';
    if (form.items.length === 0) return 'Додайте хоча б один матеріал';
    if (form.items.some((it) => !it.nomenclature_id))
      return 'Оберіть номенклатуру у всіх рядках';
    return null;
  }

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова вимога-накладна' : `Вимога: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ Проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/material-requisitions')}
          onSave={() => {
            const err = validate();
            if (err) { setError(err); return; }
            setError(null);
            (isNew ? create.mutate() : update.mutate());
          }}
          saveDisabled={create.isPending || update.isPending || isSimpleMode}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => {
            const err = validate();
            if (err) { setError(err); return; }
            setError(null);
            post.mutate();
          }}
          postDisabled={post.isPending || isSimpleMode}
          postPending={post.isPending}
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          deletable={false}
        />
      </div>

      {isSimpleMode && (
        <div style={{
          padding: '0.75rem 1rem', margin: '0.5rem 0', borderRadius: 6,
          background: '#fef3c7', border: '1px solid #fbbf24', color: '#92400e',
        }}>
          ⚠ Ця юрособа веде <b>спрощений</b> облік виробництва (без НЗВ). Вимога-накладна
          тут <b>не застосовується</b> — вона лишила б залишок на рах. 23. Списуйте матеріали
          через <b>Звіт виробництва</b>, або перемкніть облік на «Повний (НЗВ)» у картці організації.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
        <div className="field">
          <label>Юрособа</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field">
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''} disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={isPosted} />
        </div>
        <div className="field">
          <label>Склад джерело</label>
          <select value={form.source_warehouse_id}
                  onChange={(e) => setForm({ ...form, source_warehouse_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (<option key={w.id} value={w.id}>{w.code} — {w.name}</option>))}
          </select>
        </div>
        <div className="field">
          <label>Підрозділ (виробництво)</label>
          <select value={form.department_id}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— (без аналітики НЗВ)</option>
            {deptQ.data?.items.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
          </select>
        </div>
        <div className="field">
          <label>Номенклатурна група (одиниця НЗВ)</label>
          <select value={form.nomenclature_group_id}
                  onChange={(e) => setForm({ ...form, nomenclature_group_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— (без аналітики НЗВ)</option>
            {groupQ.data?.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
          </select>
        </div>
        <div className="field">
          <label>Рахунок витрат (виробництво)</label>
          {acctSelect(form.cost_account_code, (v) => setForm({ ...form, cost_account_code: v ?? '' }), '23')}
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>

      <div style={{ marginTop: '1rem', fontWeight: 600 }}>
        Матеріали для списання {isPosted && `— вартість: ${totalCost.toFixed(2)}`}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Матеріал</th>
            <th style={{ padding: '0.5rem', width: 100 }}>К-сть</th>
            <th style={{ padding: '0.5rem', width: 150 }}>Рах. обліку</th>
            <th style={{ padding: '0.5rem', width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={it.nomenclature_id}
                        onChange={(e) => setItemField(i, { nomenclature_id: e.target.value })}
                        disabled={isPosted}>
                  <option value="">—</option>
                  {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.001" value={it.quantity}
                       onChange={(e) => setItemField(i, { quantity: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                {acctSelect(it.account_code, (v) => setItemField(i, { account_code: v }), '201')}
              </td>
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && <button onClick={() => removeItem(i)} title="Видалити рядок">×</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isPosted && (
        <button onClick={addItem} style={{ marginTop: '0.5rem' }}>+ Додати рядок</button>
      )}

      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
